import React, { useRef, useState, useEffect, useCallback } from 'react';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useOnScreen from '../../hooks/useOnScreen';
import useTypewriter from '../../hooks/useTypewriter';
import creatorDemo from "../../assets/creatorDemo.png";
import leftPointer from "../../assets/leftPointer.png"
import demoVideo from "../../assets/Podcast_Intro.mp4";
import StackedCards from './StackedCards';

const TOTAL_SLIDES = 2;
const AUTO_PLAY_INTERVAL = 5000; // ms

const Carousel = () => {
  const ref = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const [slidePaused, setSlidePaused] = useState(false);

  const carouselRef = useRef(null);
  const isCarouselVisible = useOnScreen(carouselRef, { threshold: 0.3 });

  const hasBeenVisibleRef = useRef(false);

  const onCardAnimating = useCallback((isPaused) => {
    setSlidePaused(isPaused);
  }, []);

  useEffect(() => {
    if (isCarouselVisible && !hasBeenVisibleRef.current) {
      // First time entering view — reset to slide 1
      hasBeenVisibleRef.current = true;
      setCurrentSlide(0);
    }

    if (!isCarouselVisible) {
      // Left view — allow reset on next entry
      hasBeenVisibleRef.current = false;
    }
  }, [isCarouselVisible]);

  useEffect(() => {
    if (slidePaused) return; // StackedCards is animating, carousel is paused
    if (currentSlide === 1) return; // Slide 2 is managed by onCycleComplete, not a timer

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % TOTAL_SLIDES);
    }, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [slidePaused, currentSlide]);

  // Add this callback — called by StackedCards when all 4 cards have cycled
  const onStackedCardsCycleComplete = useCallback(() => {
    setCurrentSlide(0); // always go back to slide 1
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % TOTAL_SLIDES);
  const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? TOTAL_SLIDES - 1 : prev - 1));

  // ── Scroll / visibility hooks
  const { ref: demoSectionRef, isVisible: demoSectionVisible } = useScrollAnimation({ threshold: 0.2 });
  const isVisible = useOnScreen(ref, { threshold: 0.5 });

  // ── Typewriter hook
  const fullSubtitle1 = "Just drag, drop, and customize.";
  const typedSubtitle1 = useTypewriter(fullSubtitle1, 50, isVisible);

  const fullSubtitle2 = "Edit your template even \nquicker now in lite view.";
  const typedSubtitle2 = useTypewriter(fullSubtitle2, 50, isVisible && currentSlide === 1);

  const NavControls = () => (
    <div className="flex gap-4 justify-center md:justify-start mt-6 sm:mt-8">
      <button
        onClick={prevSlide}
        className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-700 hover:bg-gray-100 transition-all duration-200"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      <button
        onClick={nextSlide}
        className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-700 hover:bg-gray-100 transition-all duration-200"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="w-full relative overflow-hidden" id="carousel-section">
      <div
        className="flex w-full transition-transform duration-700 ease-in-out items-stretch"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >

        {/* ══════════════════════════════════════════════════════════════════
         *  SLIDE 1
         * ══════════════════════════════════════════════════════════════════ */}
        <div className="w-full shrink-0">
          <div
            ref={demoSectionRef}
            className={`relative overflow-hidden bg-white mt-8 sm:mt-12 md:mt-16 lg:mt-20 xl:mt-22 2xl:mt-24 py-12 sm:py-16 md:py-20 lg:py-24 flex md:flex-row flex-col gap-4 md:gap-0 w-full items-center justify-between px-4 sm:px-6 md:px-0 transition-all duration-700 ${demoSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            {/* Thick stroke with blur at the top */}
            <div className="absolute top-[-15px] sm:top-[-20px] md:top-[-25px] lg:top-[-30px] left-0 right-0 h-[40px] sm:h-[50px] md:h-[60px] lg:h-[80px] bg-ag-green blur-xl z-0"></div>

            <div className={`relative z-10 flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-6 md:ml-8 lg:ml-12 xl:ml-16 2xl:ml-20 transition-all duration-700 ${demoSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
              <p className="font-sourceSans font-black tracking-tighter text-center md:text-start text-ag-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl">
                No <span className="font-sourceSans font-light italic">animation</span>
                <span className="block">experience needed.</span>
              </p>
              <p
                ref={ref}
                className="font-medium text-center md:text-start text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-3xl text-ag-black-300"
              >
                {typedSubtitle1}
                {isVisible && typedSubtitle1.length < fullSubtitle1.length && (
                  <span className="animate-pulse text-ag-green">|</span>
                )}
              </p>

              <NavControls />
            </div>

            <div className={`relative z-10 w-full md:w-auto max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl 2xl:max-w-2xl transition-all duration-700 ${demoSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`} style={{ transitionDelay: '200ms' }}>
              <img src={creatorDemo} alt="demo" className="w-full h-auto hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
         *  SLIDE 2
         * ══════════════════════════════════════════════════════════════════ */}
        <div className="w-full shrink-0">
          <div className="relative overflow-hidden bg-white mt-8 sm:mt-12 md:mt-16 lg:mt-20 xl:mt-22 2xl:mt-24 py-12 sm:py-16 md:py-20 lg:py-24 flex md:flex-row flex-col gap-8 md:gap-12 w-full items-center px-4 sm:px-8 md:px-12 lg:px-16 xl:px-20 2xl:px-24 min-h-full">

            {/* Thick stroke with blur at the top — same as Slide 1 */}
            <div className="absolute top-[-15px] sm:top-[-20px] md:top-[-25px] lg:top-[-30px] left-0 right-0 h-[40px] sm:h-[50px] md:h-[60px] lg:h-[80px] bg-ag-green blur-xl z-0"></div>

            {/* Left content — 50% */}
            <div className="relative flex-1 z-10 w-full md:w-1/2 flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-6">
              <p className="font-sourceSans font-black tracking-tighter text-center md:text-start text-ag-black text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-5xl 2xl:text-6xl flex flex-col items-center md:items-start">
                <span className="flex flex-row items-center gap-3 text-2xl md:text-3xl lg:text-[40px] font-medium tracking-normal text-ag-black mb-2 sm:mb-4">
                  <img src={leftPointer} alt="pointer" className="w-6 h-6 object-contain" /> Lite mode
                </span>
                <span className="block mt-2 sm:mt-4">New View <span className="font-light italic pr-2">on</span></span>
                <span className="block font-light italic mt-[-10px]">Autograph.</span>
              </p>
              <p
                ref={currentSlide === 1 ? ref : null}
                className="font-medium text-center md:text-start text-base sm:text-lg md:text-xl lg:text-2xl xl:text-2xl 2xl:text-3xl text-ag-black-300 mt-2"
              >
                {typedSubtitle2.split('\n').map((line, index, arr) => (
                  <span key={index} className="block">
                    {line}
                    {index === arr.length - 1 && isVisible && currentSlide === 1 && typedSubtitle2.length < fullSubtitle2.length && (
                      <span className="animate-pulse text-ag-green">|</span>
                    )}
                  </span>
                ))}
              </p>

              <NavControls />
            </div>

            {/* Right content — 50%, stacked cards */}
            <div className="relative z-10 w-full md:w-1/2 pt-8 md:pt-0">
              <StackedCards
                demoVideo={demoVideo}
                isActive={currentSlide === 1}
                onCardAnimating={onCardAnimating}
                onCycleComplete={onStackedCardsCycleComplete}
              />
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default Carousel;